import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Table, { TableControls, TableFooter, buildBulkSelectionColumn, type TableAction } from "@/components/Table";
import { useCanManageCommitteesBool } from "@/hooks/useCapabilities";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import type { CommitteeCatalogScope } from "../committeeScope";
import {
  NATIONAL_SECTION_NAME,
  nationalCommitteeDetailPath,
  resolveNationalSectionId,
  sectionOperationsCommitteePath
} from "../constants";
import {
  CREATE_BACKOFFICE_COMMITTEE_MUTATION,
  EXPORT_BACKOFFICE_COMMITTEE_ASSIGNMENTS_MUTATION,
  UPDATE_BACKOFFICE_COMMITTEE_MUTATION
} from "../mutations";
import { BACKOFFICE_COMMITTEES_QUERY, NATIONAL_SECTION_QUERY } from "../queries";
import type {
  BackofficeCommitteesQueryResponse,
  BackofficeCommitteesQueryVariables,
  CommitteeCatalogTab,
  CommitteeOrderByInput,
  CommitteeRow,
  CommitteeStatus
} from "../types";
import usePagination from "@/hooks/usePagination";
import { useRowHighlight } from "@/hooks/useRowHighlight";
import { useParams as useUrlParams, useSearchWithParams } from "@/hooks/useSearchParams";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import type { Path } from "@/router";
import AddCommitteeParticipantsSheet from "./AddCommitteeParticipantsSheet";
import CommitteeDialog, { type CommitteeDialogMode, type CommitteeDialogSaveInput } from "./CommitteeDialog";
import CommitteeStatusChangeDialog, { committeeStatusChangeMode } from "./CommitteeStatusChangeDialog";
import { CATALOG_SORT_FIELD_MAP, getCatalogColumns } from "./TableColumns";

type CommitteeDialogState = {
  mode: CommitteeDialogMode;
  committee?: CommitteeRow;
};

type CommitteesCatalogTableProps = {
  catalogTab: CommitteeCatalogTab;
  sectionId?: string;
  sectionName?: string;
  canManageCommittees?: boolean;
  committeeScope?: CommitteeCatalogScope;
  addDialogOpen?: boolean;
  onAddDialogOpenChange?: (open: boolean) => void;
  onCanAddCommitteeChange?: (canAdd: boolean) => void;
};

export default function CommitteesCatalogTable({
  catalogTab,
  sectionId: sectionIdProp,
  sectionName,
  canManageCommittees: canManageCommitteesProp,
  committeeScope = "national",
  addDialogOpen = false,
  onAddDialogOpenChange,
  onCanAddCommitteeChange
}: CommitteesCatalogTableProps) {
  const [sorting, setSorting] = useState<SortingState>(() =>
    committeeScope === "section" ? [{ id: "status", desc: false }] : []
  );
  const [committeeDialog, setCommitteeDialog] = useState<CommitteeDialogState | null>(null);
  const [committeeDialogOpen, setCommitteeDialogOpen] = useState(false);
  const [statusChangeCommittee, setStatusChangeCommittee] = useState<CommitteeRow | null>(null);
  const [addPeopleCommittee, setAddPeopleCommittee] = useState<CommitteeRow | null>(null);
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const canManageFromPermission = useCanManageCommitteesBool();
  const canManageCommittees = canManageCommitteesProp ?? canManageFromPermission;
  const {
    rowSelection,
    setRowSelection,
    bulkSelectionProps,
    deselectAll,
    isSelectedOnly,
    rowSelectionCount,
    orderedSelectedIds
  } = useBulkSelection();
  const resolveNationalSection = !sectionIdProp;
  const { Menu, setMenu } = useTableRowMenu();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "committeeSearch"
  });
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const coreOnly = params.core === "true";

  const {
    data: nationalSectionData,
    loading: nationalSectionLoading,
    error: nationalSectionError
  } = useQuery(NATIONAL_SECTION_QUERY, {
    variables: { input: { search: NATIONAL_SECTION_NAME } },
    skip: !resolveNationalSection
  });
  const nationalSectionId = useMemo(
    () => (resolveNationalSection ? resolveNationalSectionId(nationalSectionData?.crmSections?.nodes) : undefined),
    [nationalSectionData?.crmSections?.nodes, resolveNationalSection]
  );
  const sectionId = sectionIdProp ?? nationalSectionId;
  const nationalSectionReady = !resolveNationalSection || !nationalSectionLoading;
  const nationalSectionMissing =
    resolveNationalSection && nationalSectionReady && !nationalSectionId && !nationalSectionError;
  const canAddCommittee = canManageCommittees && !!sectionId;

  useEffect(() => {
    onCanAddCommitteeChange?.(canAddCommittee);
  }, [canAddCommittee, onCanAddCommitteeChange]);

  useEffect(() => {
    resetPagination();
  }, [coreOnly, debouncedSearch, sectionId, catalogTab, resetPagination]);

  useEffect(() => {
    deselectAll();
  }, [catalogTab, coreOnly, debouncedSearch, deselectAll, sectionId]);

  useEffect(() => {
    if (!addDialogOpen) return;

    if (!sectionId) {
      onAddDialogOpenChange?.(false);
      showErrorSnackbar(resolveNationalSection ? "National Section is not available" : "Section is not available");
      return;
    }

    setCommitteeDialog({ mode: "add" });
    setCommitteeDialogOpen(true);
    onAddDialogOpenChange?.(false);
  }, [addDialogOpen, onAddDialogOpenChange, resolveNationalSection, sectionId]);

  const statusFilter: CommitteeStatus | undefined = catalogTab === "active" ? "ACTIVE" : undefined;

  const orderBy = useMemo((): CommitteeOrderByInput | undefined => {
    const sort = sorting[0];
    if (!sort) {
      if (committeeScope === "section") {
        return { field: "STATUS", direction: "ASC" };
      }
      return undefined;
    }

    const field = CATALOG_SORT_FIELD_MAP[sort.id];
    if (!field) return undefined;

    return {
      field,
      direction: sort.desc ? "DESC" : "ASC"
    };
  }, [committeeScope, sorting]);

  const variables: BackofficeCommitteesQueryVariables = {
    ...paginationVariables,
    input: {
      search: debouncedSearch || undefined,
      ...(sectionId && { sectionId }),
      ...(statusFilter && { status: statusFilter }),
      ...(coreOnly && { core: true })
    },
    orderBy
  };

  const { data, previousData, loading, error } = useQuery<
    BackofficeCommitteesQueryResponse,
    BackofficeCommitteesQueryVariables
  >(BACKOFFICE_COMMITTEES_QUERY, {
    variables,
    skip: !sectionId,
    notifyOnNetworkStatusChange: true
  });

  const [createCommittee, { loading: creatingCommittee }] = useMutation(CREATE_BACKOFFICE_COMMITTEE_MUTATION, {
    refetchQueries: [BACKOFFICE_COMMITTEES_QUERY]
  });

  const [updateCommittee, { loading: updatingCommittee }] = useMutation(UPDATE_BACKOFFICE_COMMITTEE_MUTATION, {
    refetchQueries: [BACKOFFICE_COMMITTEES_QUERY]
  });
  const [exportCommitteeAssignments, { loading: exporting }] = useMutation(
    EXPORT_BACKOFFICE_COMMITTEE_ASSIGNMENTS_MUTATION
  );

  const savingCommittee = creatingCommittee || updatingCommittee;

  const handleCommitteeDialogClosed = useCallback(() => {
    setCommitteeDialogOpen(false);
    setCommitteeDialog(null);
  }, []);

  const requestCloseCommitteeDialog = useCallback(() => {
    setCommitteeDialogOpen(false);
  }, []);

  const closeStatusChangeDialog = useCallback(() => {
    setStatusChangeCommittee(null);
  }, []);

  const handleCommitteeDialogSave = useCallback(
    async (input: CommitteeDialogSaveInput) => {
      try {
        if (input.mode === "add") {
          if (!sectionId) {
            showErrorSnackbar(
              resolveNationalSection ? "National Section is not available" : "Section is not available"
            );
            return;
          }

          const { data: mutationData } = await createCommittee({
            variables: {
              input: {
                name: input.name,
                sectionId,
                status: input.status,
                core: input.core
              }
            }
          });

          const result = mutationData?.createBackofficeCommittee;
          if (!result?.success) {
            showErrorSnackbar(result?.message ?? "Unable to add committee");
            return;
          }

          showSnackbar("Committee added");
          requestCloseCommitteeDialog();
          return;
        }

        const { data: mutationData } = await updateCommittee({
          variables: {
            input: {
              id: input.committeeId,
              name: input.name,
              status: input.status,
              core: input.core
            }
          }
        });

        const result = mutationData?.updateBackofficeCommittee;
        if (!result?.success) {
          showErrorSnackbar(result?.message ?? "Unable to update committee");
          return;
        }

        showSnackbar("Committee updated");
        requestCloseCommitteeDialog();
      } catch (error) {
        showErrorSnackbar((error as Error).message);
      }
    },
    [createCommittee, requestCloseCommitteeDialog, resolveNationalSection, sectionId, updateCommittee]
  );

  const handleStatusChangeConfirm = useCallback(async () => {
    if (!statusChangeCommittee) return;

    const nextStatus = statusChangeCommittee.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    try {
      const { data: mutationData } = await updateCommittee({
        variables: {
          input: {
            id: statusChangeCommittee.id,
            status: nextStatus
          }
        }
      });

      const result = mutationData?.updateBackofficeCommittee;
      if (!result?.success) {
        showErrorSnackbar(result?.message ?? "Unable to update committee status");
        return;
      }

      showSnackbar(nextStatus === "INACTIVE" ? "Committee deactivated" : "Committee activated");
      closeStatusChangeDialog();
    } catch (error) {
      showErrorSnackbar((error as Error).message);
    }
  }, [closeStatusChangeDialog, statusChangeCommittee, updateCommittee]);

  const connection = data?.backofficeCommittees ?? previousData?.backofficeCommittees;
  const rows: CommitteeRow[] = useMemo(() => {
    const nodes = connection?.nodes ?? [];
    if (committeeScope !== "section") return nodes;

    const primarySort = sorting[0];
    if (primarySort && primarySort.id !== "status") return nodes;

    const statusDirection = primarySort?.desc ? -1 : 1;
    return [...nodes].sort((a, b) => {
      const statusCmp = a.status.localeCompare(b.status) * statusDirection;
      if (statusCmp !== 0) return statusCmp;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
  }, [committeeScope, connection?.nodes, sorting]);
  const displayRows = useMemo(
    () => (isSelectedOnly ? rows.filter((row) => rowSelection[row.id]) : rows),
    [isSelectedOnly, rowSelection, rows]
  );

  const handleEditCommittee = useCallback((row: CommitteeRow) => {
    setCommitteeDialog({ mode: "edit", committee: row });
    setCommitteeDialogOpen(true);
  }, []);

  const handleAddPeople = useCallback((row: CommitteeRow) => {
    setAddPeopleCommittee(row);
  }, []);

  const handleAddPeopleClosed = useCallback(() => {
    setAddPeopleCommittee(null);
  }, []);

  const handleStatusChangeClick = useCallback((row: CommitteeRow) => {
    setStatusChangeCommittee(row);
  }, []);

  const handleExport = useCallback(async () => {
    if (exporting || orderedSelectedIds.length === 0) return;

    try {
      const { data: mutationData } = await exportCommitteeAssignments({
        variables: {
          input: {
            committeeIds: orderedSelectedIds
          }
        }
      });
      const result = mutationData?.exportBackofficeCommitteeAssignments;

      if (!result?.success || !result.url) {
        showErrorSnackbar(result?.message ?? "Unable to export committee assignments");
        return;
      }

      showSnackbar(result.message || "Export generated");
      deselectAll();
      window.location.assign(result.url);
    } catch (error) {
      showErrorSnackbar((error as Error).message);
    }
  }, [deselectAll, exportCommitteeAssignments, exporting, orderedSelectedIds]);

  const bulkActions = useMemo<TableAction[]>(
    () => [
      {
        label: "Export CSV",
        icon: "download",
        variant: "outlined",
        onClick: handleExport,
        disabled: exporting,
        testId: "export-committee-assignments-button"
      }
    ],
    [exporting, handleExport]
  );

  const columns = useMemo(
    () => [
      buildBulkSelectionColumn<CommitteeRow>(),
      ...getCatalogColumns({
        ...(canManageCommittees
          ? {
              canManage: true,
              setMenu,
              onAddPeople: handleAddPeople,
              onEdit: handleEditCommittee,
              onStatusChange: handleStatusChangeClick
            }
          : {})
      })
    ],
    [canManageCommittees, handleAddPeople, handleEditCommittee, handleStatusChangeClick, setMenu]
  );

  const hasActiveFilters = !!debouncedSearch || coreOnly || isSelectedOnly;
  const totalRowCount = connection?.totalCount ?? rows.length;
  const { highlightRowId, containerRef } = useRowHighlight({
    dataReady: !loading && (!resolveNationalSection || !nationalSectionLoading) && rows.length > 0
  });

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((currentSorting) =>
      typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue
    );
    resetPagination();
  };

  const toggleCoreFilter = useCallback(() => {
    if (coreOnly) {
      deleteSearchParams("core");
    } else {
      addSearchParam("core", "true");
    }
    resetPagination();
  }, [addSearchParam, coreOnly, deleteSearchParams, resetPagination]);

  if (nationalSectionError) {
    return (
      <EmptyState title="Unable to Load National Section" caption={nationalSectionError.message} iconName="error" />
    );
  }

  if (nationalSectionMissing) {
    return (
      <EmptyState
        title="National Section Not Found"
        caption="The National Section record is required to load national committees."
        iconName="error"
      />
    );
  }

  if (error) {
    return <EmptyState title="Unable to Load Committees" caption={error.message} iconName="error" />;
  }

  return (
    <>
      <div ref={containerRef}>
        <Table
          data={displayRows}
          columns={columns}
          loading={loading || (resolveNationalSection && nationalSectionLoading)}
          enableRowSelection
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          sortingState={sorting}
          onSortingChange={handleSortingChange}
          serverSideSorting
          getRowLink={(row) => {
            if (committeeScope === "section") {
              if (!sectionIdProp) return null;
              return { to: sectionOperationsCommitteePath(sectionIdProp, row.original.id) as Path };
            }

            return { to: nationalCommitteeDetailPath(row.original.id, catalogTab) as Path };
          }}
          data-testid={catalogTab === "active" ? "active-committees-table" : "all-committees-table"}
          getRowClasses={
            highlightRowId ? (row) => (row.original.name === highlightRowId ? "row-highlight" : "") : undefined
          }
          hasActiveFilters={hasActiveFilters}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Committees"
              caption="Committees will appear here when they are available."
              iconName="groups"
            />
          )}
          renderFilteredEmptyState={() =>
            isSelectedOnly ? (
              <EmptyState
                title="No Selected Rows on This Page"
                caption="Selected committees on other pages are not shown here. Turn off Show Selected Only to return to the full list."
                iconName="groups"
              />
            ) : (
              <EmptyState
                title="No Matching Committees"
                caption="Try a different search or filter."
                iconName="search"
              />
            )
          }
          renderControls={(table, { onSortClick } = {}) => (
            <TableControls
              pagination={createHeaderPagination(connection, {
                loading: loading,
                pageCount: rows.length,
                totalCount: totalRowCount
              })}
              onSortClick={onSortClick}
              bulkActions={bulkActions}
              {...bulkSelectionProps}
              onDeselectAll={deselectAll}
              totalRowCount={rowSelectionCount}
              onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
              allRowsSelected={table.getIsAllRowsSelected()}
              someRowsSelected={table.getIsSomeRowsSelected()}
              useExpandedFilters
              expandedFiltersState={{ core: coreOnly }}
              activeFilters={[debouncedSearch, coreOnly].filter(Boolean).length}
              filterChips={[
                {
                  label: "Core Committees",
                  onClick: toggleCoreFilter,
                  selected: coreOnly
                }
              ]}
              onClearAllFilters={() => {
                clearSearch();
                deleteSearchParams("core");
                resetPagination();
              }}
            >
              <SearchInput
                query={searchValue}
                onChange={(value) => {
                  setSearchValue(value);
                  resetPagination();
                }}
                onClear={() => {
                  clearSearch();
                  resetPagination();
                }}
                placeholder="Search Committees"
              />
            </TableControls>
          )}
          renderFooter={() => (
            <TableFooter
              pagination={createFooterPagination(connection, {
                loading: loading,
                pageCount: rows.length,
                totalCount: totalRowCount
              })}
              totalRowCount={totalRowCount}
              {...bulkSelectionProps}
            />
          )}
        />
      </div>
      <Menu />
      <CommitteeDialog
        open={committeeDialogOpen}
        mode={committeeDialog?.mode ?? "add"}
        initialCommittee={committeeDialog?.committee}
        saving={savingCommittee}
        onClose={handleCommitteeDialogClosed}
        onSave={handleCommitteeDialogSave}
      />
      <CommitteeStatusChangeDialog
        open={statusChangeCommittee != null}
        committee={statusChangeCommittee}
        mode={statusChangeCommittee ? committeeStatusChangeMode(statusChangeCommittee.status) : "deactivate"}
        saving={updatingCommittee}
        onClose={closeStatusChangeDialog}
        onConfirm={handleStatusChangeConfirm}
      />
      <AddCommitteeParticipantsSheet
        open={addPeopleCommittee != null}
        committeeId={addPeopleCommittee?.id}
        committeeName={addPeopleCommittee?.name ?? ""}
        committeeScope={committeeScope}
        sectionName={sectionName}
        onClose={handleAddPeopleClosed}
      />
    </>
  );
}
